import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Layout, Search, Star, Users, Calendar, Filter, Grid, List,
  ChevronDown, ArrowRight, Check, Loader2, X, ThumbsUp, MessageSquare,
  Copy, ExternalLink, Eye, EyeOff
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category definitions
const CATEGORIES = {
  conference: { label: 'Conference', color: 'bg-purple-100 text-purple-700', icon: '🎤' },
  workshop: { label: 'Workshop', color: 'bg-blue-100 text-blue-700', icon: '🛠️' },
  tournament: { label: 'Tournament', color: 'bg-green-100 text-green-700', icon: '🏆' },
  general: { label: 'General', color: 'bg-gray-100 text-gray-700', icon: '📋' }
};

// Star Rating Component
const StarRating = ({ rating, size = 'sm', onRate, interactive = false }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => interactive && onRate && onRate(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`${interactive ? 'cursor-pointer' : 'cursor-default'}`}
          disabled={!interactive}
        >
          <Star 
            className={`${sizeClass} ${
              star <= (hoverRating || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Template Card Component
const TemplateCard = ({ template, viewMode, onUse, onRate }) => {
  const [showDetails, setShowDetails] = useState(false);
  const category = CATEGORIES[template.category] || CATEGORIES.general;
  const fields = template.fields_config || [];
  const isCompact = viewMode === 'compact';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-300 group ${
        isCompact ? '' : ''
      }`}
      data-testid={`marketplace-template-${template.id}`}
    >
      {/* Header */}
      <div className="p-5 border-b border-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{category.icon}</span>
            <div>
              <h3 className="font-semibold text-white group-hover:text-primary transition-colors">
                {template.name}
              </h3>
              <span className={`text-xs px-2 py-0.5 rounded ${category.color}`}>
                {category.label}
              </span>
            </div>
          </div>
          {template.owner_type === 'system' && (
            <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">
              Official
            </span>
          )}
        </div>
        
        {template.description && (
          <p className="text-sm text-white/60 line-clamp-2 mb-3">
            {template.description}
          </p>
        )}
        
        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-white/50">
          <div className="flex items-center gap-1">
            <StarRating rating={template.average_rating} />
            <span className="ml-1">{template.average_rating?.toFixed(1) || '0.0'}</span>
            <span>({template.total_ratings || 0})</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{template.usage_count || 0} uses</span>
          </div>
        </div>
      </div>

      {/* Fields Preview - Toggle for detailed view */}
      {!isCompact && (
        <div className="p-5 bg-background/50">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center justify-between w-full text-sm text-white/70 hover:text-white mb-3"
          >
            <span className="font-medium">{fields.length} Fields</span>
            <span className="flex items-center gap-1">
              {showDetails ? 'Hide' : 'Show'} Details
              {showDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </span>
          </button>
          
          <AnimatePresence>
            {showDetails ? (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-2 overflow-hidden"
              >
                {fields.map((field, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-2 bg-card rounded-lg text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-primary/20 text-primary rounded text-xs flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="text-white">{field.field_label}</span>
                      {field.is_required && (
                        <span className="text-red-400 text-xs">*</span>
                      )}
                    </div>
                    <span className="text-white/40 text-xs capitalize">{field.field_type}</span>
                  </div>
                ))}
              </motion.div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {fields.slice(0, 4).map((field, idx) => (
                  <span key={idx} className="px-2 py-1 bg-card rounded text-xs text-white/60">
                    {field.field_label}
                  </span>
                ))}
                {fields.length > 4 && (
                  <span className="px-2 py-1 bg-card rounded text-xs text-white/40">
                    +{fields.length - 4} more
                  </span>
                )}
              </div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Compact view - just field count */}
      {isCompact && (
        <div className="px-5 py-3 bg-background/50">
          <div className="flex flex-wrap gap-1">
            {fields.slice(0, 3).map((field, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-card rounded text-xs text-white/60">
                {field.field_label}
              </span>
            ))}
            {fields.length > 3 && (
              <span className="px-2 py-0.5 bg-card rounded text-xs text-white/40">
                +{fields.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="p-4 border-t border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40">By {template.creator_name || 'Community'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onRate(template)}
            className="text-white/60 hover:text-white"
          >
            <Star className="w-4 h-4 mr-1" />
            Rate
          </Button>
          <Button 
            size="sm" 
            onClick={() => onUse(template)}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            Use Template
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Rating Modal
const RatingModal = ({ isOpen, onClose, template, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!rating || !email) {
      toast.error('Please provide a rating and your email');
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit({
        rating,
        comment,
        user_email: email,
        user_name: name
      });
      onClose();
    } catch (error) {
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-xl w-full max-w-md overflow-hidden"
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-white">Rate This Template</h2>
          <p className="text-sm text-white/60 mt-1">{template?.name}</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Star Rating */}
          <div className="text-center">
            <p className="text-sm text-white/60 mb-3">How would you rate this template?</p>
            <div className="flex justify-center">
              <StarRating rating={rating} size="lg" interactive onRate={setRating} />
            </div>
            {rating > 0 && (
              <p className="text-sm text-white/60 mt-2">
                {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Comment (optional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-4 py-2 text-white placeholder-white/40 focus:ring-2 focus:ring-primary"
              placeholder="Share your experience..."
              rows={3}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Your Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-4 py-2 text-white placeholder-white/40 focus:ring-2 focus:ring-primary"
              placeholder="you@example.com"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Your Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-4 py-2 text-white placeholder-white/40 focus:ring-2 focus:ring-primary"
              placeholder="John Doe"
            />
          </div>
        </div>

        <div className="p-6 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            className="flex-1 bg-primary text-white"
            onClick={handleSubmit}
            disabled={submitting || !rating}
          >
            {submitting ? 'Submitting...' : 'Submit Rating'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Main Marketplace Page
const TemplateMarketplacePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('detailed'); // detailed or compact
  
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    category: searchParams.get('category') || '',
    sortBy: searchParams.get('sort') || 'popular'
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0
  });

  const [ratingModal, setRatingModal] = useState({ open: false, template: null });

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      params.append('sort_by', filters.sortBy);
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);

      const res = await fetch(`${API}/universal-events/marketplace/templates?${params}`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('q', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.sortBy !== 'popular') params.set('sort', filters.sortBy);
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleUseTemplate = (template) => {
    // Navigate to event wizard with template pre-selected
    const templateJson = encodeURIComponent(JSON.stringify(template.fields_config));
    window.location.href = `/admin/events/new?template=${template.id}`;
  };

  const handleRateTemplate = async (data) => {
    const template = ratingModal.template;
    const res = await fetch(`${API}/universal-events/templates/${template.id}/rate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    
    if (res.ok) {
      toast.success('Thank you for your rating!');
      fetchTemplates(); // Refresh to show updated rating
    } else {
      throw new Error('Failed to submit rating');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="template-marketplace-page">
      <MegaMenu />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Template Marketplace
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
            Browse community-created registration form templates. Find the perfect starting point for your next event.
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="Search templates..."
              className="w-full bg-card border border-border rounded-xl pl-12 pr-4 py-4 text-white placeholder-white/40 focus:ring-2 focus:ring-primary"
              data-testid="marketplace-search"
            />
          </div>
        </div>
      </section>

      {/* Filters & Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              {/* Category Filter */}
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="bg-card border border-border rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-primary"
              >
                <option value="">All Categories</option>
                {Object.entries(CATEGORIES).map(([key, cat]) => (
                  <option key={key} value={key}>{cat.icon} {cat.label}</option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                className="bg-card border border-border rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-primary"
              >
                <option value="popular">Most Popular</option>
                <option value="highest_rated">Highest Rated</option>
                <option value="newest">Newest</option>
              </select>
              
              <span className="text-white/50 text-sm">
                {loading ? 'Loading...' : `${templates.length} templates`}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <button
                onClick={() => setViewMode('detailed')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'detailed' ? 'bg-primary text-white' : 'text-white/50 hover:text-white'
                }`}
                title="Detailed View"
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('compact')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'compact' ? 'bg-primary text-white' : 'text-white/50 hover:text-white'
                }`}
                title="Compact View"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Templates Grid */}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-xl overflow-hidden animate-pulse">
                  <div className="p-5">
                    <div className="h-6 bg-white/5 rounded mb-3 w-2/3" />
                    <div className="h-4 bg-white/5 rounded mb-2 w-full" />
                    <div className="h-4 bg-white/5 rounded w-3/4" />
                  </div>
                  <div className="p-5 bg-background/50">
                    <div className="flex gap-2">
                      {[1, 2, 3].map(j => (
                        <div key={j} className="h-6 bg-white/5 rounded w-20" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16">
              <Layout className="w-16 h-16 mx-auto text-white/20 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No templates found</h3>
              <p className="text-white/60 mb-6">Try adjusting your filters</p>
              <Button 
                variant="outline" 
                onClick={() => setFilters({ search: '', category: '', sortBy: 'popular' })}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className={`grid gap-6 ${
              viewMode === 'compact' ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3'
            }`}>
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  viewMode={viewMode}
                  onUse={handleUseTemplate}
                  onRate={(t) => setRatingModal({ open: true, template: t })}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-white/60">
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
              </span>
              <Button
                variant="outline"
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Have a great template idea?
          </h2>
          <p className="text-white/60 mb-6">
            Create your own event and share your registration form template with the community.
          </p>
          <Link to="/admin/events/new">
            <Button className="bg-primary hover:bg-primary/90 text-white">
              Create New Event
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />

      {/* Rating Modal */}
      <RatingModal
        isOpen={ratingModal.open}
        onClose={() => setRatingModal({ open: false, template: null })}
        template={ratingModal.template}
        onSubmit={handleRateTemplate}
      />
    </div>
  );
};

export default TemplateMarketplacePage;
